import { activityTypes } from '../../../models/design/activities/activity-type.model';
import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityReference, Storey } from '@shared/models/design/storey.model';
import { findDuplicates } from '../../array.helpers';
import {
  GenericValidationError,
  StoreyValidationError,
  ValidationResult,
} from '../../../validation/validation.models';
import { guards } from '@shared/helpers/typeguards.helpers';
import { sum } from '@shared/helpers/math.helpers';
import { activityRefGuards } from '../guards/activity-guards.helpers';

export const ActivityReferencesValidationErrors = {
  STOREY_ACTIVITIES_ARRAY: 'Storey activities must be an array',
  STOREY_ACTIVITY_INVALID_REF: 'Storey references missing or invalid activity',
  STOREY_ACTIVITY_DUPLICATE_REF: 'Storey has duplicate activity references',
  STOREY_ACTIVITY_SHARE_RANGE: 'Activity share must be between 0 and 1',
  STOREY_ACTIVITY_SHARE_CALCULATED:
    'Calculated activity references must not have a share',
  STOREY_ACTIVITY_SHARE_SUM: 'Sum of activity shares on storey cannot exceed 1',
  STOREY_ACTIVITY_TYPE_INVALID: 'Activity reference has invalid type',
  STOREY_ACTIVITY_ID_INVALID: 'Activity reference has invalid id',
  STOREY_ACTIVITY_DUPLICATE_TYPE: 'Storey has duplicate activity types',
} as const;

/**
 * Validate activity references on a storey
 * @param activityRefs Activity references to validate
 * @param activities If activities are provided, validate the activity references else ignore them
 * @returns ValidationResult
 */
export const isValidActivityReferences = (
  storey: Storey,
  activities?: Activity[],
): ValidationResult => {
  const { activities: activityRefs } = storey;
  if (!guards.is.array(activityRefs)) {
    return new StoreyValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITIES_ARRAY,
      { storey },
    );
  }

  // Check each activity reference is valid
  for (const ref of activityRefs) {
    const validRef = isValidActivityReference(ref);
    if (validRef !== true) return validRef;
  }

  const activityIds = activityRefs.map((r) => r.activity_id);
  const duplicateRefs = findDuplicates(activityIds);
  if (duplicateRefs.length) {
    return new StoreyValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_DUPLICATE_REF,
      { storey },
    );
  }

  // Currently we don't allow duplicate activity types on a storey. TODO: Ensure everything is working by id before removing this.
  const activityTypes = activityRefs.map((r) => r.type);
  const duplicateTypes = findDuplicates(activityTypes);
  if (duplicateTypes.length) {
    return new StoreyValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_DUPLICATE_TYPE,
      { storey },
    );
  }

  // If activities are provided, validate so activity references are pointing to existing activities
  if (activities) {
    for (const ref of activityRefs) {
      const activity = activities.find((a) => a.id === ref.activity_id);
      if (!activity) {
        return new StoreyValidationError(
          ActivityReferencesValidationErrors.STOREY_ACTIVITY_INVALID_REF,
          { storey },
        );
      }
    }
  }

  // Check if the sum of the shares is 1 (allow for small rounding errors)
  if (sum(activityRefs, 'share') > 1 + 1e-9) {
    return new StoreyValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_SHARE_SUM,
      { storey },
    );
  }

  return true;
};

export const isValidActivityReference = (
  activityRef: ActivityReference,
): ValidationResult => {
  const { type, id, share } = activityRef;
  const isValidShareNumber =
    guards.is.number(share) && guards.is.numberBetween(share, 0, 1);
  const isCalculatedAreaRef =
    activityRefGuards.is.calculatedAreaActivityRef(activityRef);

  // Type must be one of ActivityType
  if (!activityTypes.includes(type)) {
    return new GenericValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_TYPE_INVALID,
    );
  }

  // Id must be a string.
  if (!guards.is.string(id)) {
    return new GenericValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_ID_INVALID,
    );
  }

  // If the activity is calculated, it doesn't have a share
  if (isCalculatedAreaRef && guards.is.number(share)) {
    return new GenericValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_SHARE_CALCULATED,
    );
  }

  // Program activities must have a share between 0 and 1.
  if (!isCalculatedAreaRef && !isValidShareNumber) {
    return new GenericValidationError(
      ActivityReferencesValidationErrors.STOREY_ACTIVITY_SHARE_RANGE,
    );
  }
  return true;
};
